import React, { useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext, API } from '../App';
import { Navigation } from '../components/Navigation';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import CourseSearchInput from '../components/CourseSearchInput';
import {
  MapPin, Users, Calendar, Clock, PlusCircle, Loader2, Check, X,
  UserCheck, UserMinus, Trash2, Flag
} from 'lucide-react';
import { toast } from 'sonner';

export default function NearbyGolfers() {
  const { user, loading } = useContext(AuthContext);
  const navigate = useNavigate();

  const [teeTimes, setTeeTimes] = useState([]);
  const [loadingTimes, setLoadingTimes] = useState(true);
  const [showPostForm, setShowPostForm] = useState(false);
  const [posting, setPosting] = useState(false);
  const [joiningId, setJoiningId] = useState(null);

  const [form, setForm] = useState({
    course_name: '',
    date: '',
    time: '',
    max_players: '4',
    description: '',
    skill_level_preference: ''
  });

  useEffect(() => {
    if (!loading && !user) navigate('/');
    if (!loading && user && !user.is_member) {
      toast.error('Active membership required');
      navigate('/membership');
    }
  }, [loading, user, navigate]);

  useEffect(() => {
    if (user) fetchTeeTimes();
  }, [user]);

  // Deep-link from Welcome card: open post form pre-filled with description
  const location = useLocation();
  useEffect(() => {
    const st = location.state;
    if (st?.openPostForm) {
      setShowPostForm(true);
      if (st.prefillDescription) {
        setForm(prev => ({ ...prev, description: st.prefillDescription }));
      }
      // Clear the state so refresh doesn't re-trigger
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const fetchTeeTimes = async () => {
    setLoadingTimes(true);
    try {
      const res = await fetch(`${API}/rounds/open`, { credentials: 'include' });
      if (res.ok) setTeeTimes(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoadingTimes(false); }
  };

  const handlePost = async () => {
    if (!form.course_name || !form.date || !form.time) {
      toast.error('Course, date, and time are required');
      return;
    }
    setPosting(true);
    try {
      const res = await fetch(`${API}/rounds/open`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          course_name: form.course_name,
          date: form.date,
          time: form.time,
          max_players: parseInt(form.max_players) || 4,
          description: form.description || null,
          skill_level_preference: form.skill_level_preference || null
        })
      });
      if (res.ok) {
        toast.success('Tee time posted! Members are being notified.');
        setShowPostForm(false);
        setForm({ course_name: '', date: '', time: '', max_players: '4', description: '', skill_level_preference: '' });
        fetchTeeTimes();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to post');
      }
    } catch { toast.error('Failed to post tee time'); }
    finally { setPosting(false); }
  };

  const handleJoin = async (roundId) => {
    setJoiningId(roundId);
    try {
      const res = await fetch(`${API}/rounds/open/${roundId}/join`, {
        method: 'POST', credentials: 'include'
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        fetchTeeTimes();
      } else {
        toast.error(data.detail || 'Failed to join');
      }
    } catch { toast.error('Failed to join'); }
    finally { setJoiningId(null); }
  };

  const handleLeave = async (roundId) => {
    try {
      const res = await fetch(`${API}/rounds/open/${roundId}/leave`, {
        method: 'POST', credentials: 'include'
      });
      if (res.ok) {
        toast.success('Left tee time');
        fetchTeeTimes();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to leave');
      }
    } catch { toast.error('Failed to leave'); }
  };

  const handleCancel = async (roundId) => {
    if (!window.confirm('Cancel this tee time?')) return;
    try {
      const res = await fetch(`${API}/rounds/open/${roundId}`, {
        method: 'DELETE', credentials: 'include'
      });
      if (res.ok) {
        toast.success('Tee time cancelled');
        fetchTeeTimes();
      }
    } catch { toast.error('Failed to cancel'); }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-bundle-cream flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-bundle-green" />
      </div>
    );
  }

  const openTimes = teeTimes.filter(t => t.status === 'open');
  const confirmedTimes = teeTimes.filter(t => t.status === 'full');

  return (
    <div className="min-h-screen bg-bundle-cream">
      <Navigation user={user} />
      <div className="max-w-2xl mx-auto px-4 pt-20 md:pt-24 pb-mobile-nav md:pb-8">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-bundle-green">Golfers Near Me</h1>
            <p className="font-body text-sm text-gray-500 mt-1">Book a tee time & fill your group</p>
          </div>
          <Button
            data-testid="post-tee-time-btn"
            onClick={() => setShowPostForm(true)}
            className="bg-bundle-green text-white hover:bg-bundle-green/90 rounded-xl gap-2"
          >
            <PlusCircle className="w-4 h-4" />
            Post Tee Time
          </Button>
        </div>

        {/* Open Tee Times */}
        {loadingTimes ? (
          <div className="text-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-bundle-green mx-auto" />
          </div>
        ) : openTimes.length === 0 && confirmedTimes.length === 0 ? (
          <Card className="bg-white border-bundle-cream-dark">
            <CardContent className="py-16 text-center">
              <Flag className="w-12 h-12 text-gray-200 mx-auto mb-4" />
              <h3 className="font-heading text-lg font-semibold text-gray-700 mb-2">No tee times posted yet</h3>
              <p className="font-body text-sm text-gray-500 mb-4">Be the first — post a tee time and invite the club!</p>
              <Button onClick={() => setShowPostForm(true)} className="bg-bundle-green text-white rounded-xl gap-2">
                <PlusCircle className="w-4 h-4" /> Post Tee Time
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {/* Open section */}
            {openTimes.length > 0 && (
              <>
                <h2 className="font-heading font-semibold text-sm text-gray-500 uppercase tracking-wider">Open — Looking for Players</h2>
                {openTimes.map(t => (
                  <TeeTimeCard
                    key={t.round_id}
                    teeTime={t}
                    user={user}
                    joiningId={joiningId}
                    onJoin={handleJoin}
                    onLeave={handleLeave}
                    onCancel={handleCancel}
                  />
                ))}
              </>
            )}

            {/* Confirmed section */}
            {confirmedTimes.length > 0 && (
              <>
                <h2 className="font-heading font-semibold text-sm text-gray-500 uppercase tracking-wider mt-8">Confirmed — Groups Set</h2>
                {confirmedTimes.map(t => (
                  <TeeTimeCard
                    key={t.round_id}
                    teeTime={t}
                    user={user}
                    joiningId={joiningId}
                    onJoin={handleJoin}
                    onLeave={handleLeave}
                    onCancel={handleCancel}
                  />
                ))}
              </>
            )}
          </div>
        )}
      </div>

      {/* Post Tee Time Dialog */}
      <Dialog open={showPostForm} onOpenChange={setShowPostForm}>
        <DialogContent className="max-w-md mx-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-bundle-green">Post a Tee Time</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="font-body text-sm font-semibold text-gray-700">Course</Label>
              <CourseSearchInput
                value={form.course_name}
                onChange={(val) => setForm(prev => ({ ...prev, course_name: val }))}
                placeholder="Search courses..."
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="font-body text-sm font-semibold text-gray-700">Date</Label>
                <Input
                  data-testid="tee-time-date"
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm(prev => ({ ...prev, date: e.target.value }))}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div>
                <Label className="font-body text-sm font-semibold text-gray-700">Time</Label>
                <Input
                  data-testid="tee-time-time"
                  type="time"
                  value={form.time}
                  onChange={(e) => setForm(prev => ({ ...prev, time: e.target.value }))}
                />
              </div>
            </div>
            <div>
              <Label className="font-body text-sm font-semibold text-gray-700">Spots Needed</Label>
              <Select value={form.max_players} onValueChange={(v) => setForm(prev => ({ ...prev, max_players: v }))}>
                <SelectTrigger data-testid="tee-time-spots"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="2">2 players (need 1)</SelectItem>
                  <SelectItem value="3">3 players (need 2)</SelectItem>
                  <SelectItem value="4">4 players (need 3)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-body text-sm font-semibold text-gray-700">Notes (optional)</Label>
              <Textarea
                data-testid="tee-time-description"
                value={form.description}
                onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Promo codes, cart info, walking only, skill level, special instructions..."
                className="resize-none"
                rows={3}
              />
            </div>
            <Button
              data-testid="submit-tee-time-btn"
              onClick={handlePost}
              disabled={posting || !form.course_name || !form.date || !form.time}
              className="w-full bg-bundle-green text-white hover:bg-bundle-green/90 rounded-xl h-12"
            >
              {posting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Post & Notify Members'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TeeTimeCard({ teeTime: t, user, joiningId, onJoin, onLeave, onCancel }) {
  const isOrganizer = t.organizer_id === user.user_id;
  const isJoined = t.current_players?.includes(user.user_id);
  const isFull = t.status === 'full';
  const spotsLeft = t.max_players - (t.current_players?.length || 0);
  const dateStr = t.date ? new Date(t.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : t.date;

  return (
    <Card
      data-testid={`tee-time-card-${t.round_id}`}
      className={`bg-white border-bundle-cream-dark overflow-hidden transition-all ${isFull ? 'border-l-4 border-l-bundle-green' : ''}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          {/* Date block */}
          <div className="flex-shrink-0 w-14 h-14 bg-bundle-cream rounded-xl flex flex-col items-center justify-center">
            <span className="font-body text-[10px] text-bundle-gold-dark uppercase">
              {t.date ? new Date(t.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short' }) : ''}
            </span>
            <span className="font-heading text-xl font-bold text-bundle-green">
              {t.date ? new Date(t.date + 'T00:00:00').getDate() : ''}
            </span>
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-heading text-base font-bold text-bundle-green truncate">{t.course_name}</h3>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                  <span className="flex items-center gap-1 font-body text-xs text-gray-500">
                    <Clock className="w-3.5 h-3.5" /> {t.time}
                  </span>
                  <span className="flex items-center gap-1 font-body text-xs text-gray-500">
                    <Calendar className="w-3.5 h-3.5" /> {dateStr}
                  </span>
                </div>
              </div>
              {/* Status badge */}
              {isFull ? (
                <span className="flex-shrink-0 px-2 py-0.5 bg-bundle-green/10 text-bundle-green text-[10px] font-bold rounded-full flex items-center gap-1">
                  <Check className="w-3 h-3" /> Confirmed
                </span>
              ) : (
                <span className="flex-shrink-0 px-2 py-0.5 bg-bundle-gold/15 text-bundle-gold-dark text-[10px] font-bold rounded-full">
                  {spotsLeft} spot{spotsLeft !== 1 ? 's' : ''} open
                </span>
              )}
            </div>

            {/* Description */}
            {t.description && (
              <p className="font-body text-xs text-gray-600 mt-2">{t.description}</p>
            )}

            {/* Players */}
            <div className="flex items-center gap-2 mt-3">
              <Users className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
              <div className="flex items-center gap-1 flex-wrap">
                {(t.players || []).map((p, i) => (
                  <span key={p.user_id} className="flex items-center gap-1">
                    {p.picture ? (
                      <img src={p.picture} alt="" className="w-5 h-5 rounded-full object-cover" />
                    ) : (
                      <span className="w-5 h-5 rounded-full bg-bundle-green/10 flex items-center justify-center text-[8px] font-bold text-bundle-green">
                        {p.name?.charAt(0)}
                      </span>
                    )}
                    <span className="font-body text-xs text-gray-700">
                      {p.name}{p.user_id === t.organizer_id ? ' (host)' : ''}{i < (t.players?.length || 0) - 1 ? ',' : ''}
                    </span>
                  </span>
                ))}
                {!isFull && (
                  <span className="font-body text-xs text-gray-400 italic">
                    + {spotsLeft} needed
                  </span>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 mt-3">
              {!isJoined && !isFull && (
                <Button
                  data-testid={`join-tee-time-${t.round_id}`}
                  onClick={() => onJoin(t.round_id)}
                  disabled={joiningId === t.round_id}
                  size="sm"
                  className="bg-bundle-green text-white hover:bg-bundle-green/90 rounded-lg text-xs gap-1 h-8"
                >
                  {joiningId === t.round_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserCheck className="w-3 h-3" />}
                  I'm In
                </Button>
              )}
              {isJoined && !isOrganizer && (
                <Button
                  data-testid={`leave-tee-time-${t.round_id}`}
                  onClick={() => onLeave(t.round_id)}
                  variant="outline"
                  size="sm"
                  className="text-red-500 border-red-200 hover:bg-red-50 rounded-lg text-xs gap-1 h-8"
                >
                  <UserMinus className="w-3 h-3" /> Leave
                </Button>
              )}
              {isOrganizer && (
                <Button
                  data-testid={`cancel-tee-time-${t.round_id}`}
                  onClick={() => onCancel(t.round_id)}
                  variant="outline"
                  size="sm"
                  className="text-red-500 border-red-200 hover:bg-red-50 rounded-lg text-xs gap-1 h-8"
                >
                  <Trash2 className="w-3 h-3" /> Cancel
                </Button>
              )}
              {isJoined && (
                <span className="font-body text-xs text-bundle-green flex items-center gap-1">
                  <Check className="w-3 h-3" /> You're in
                </span>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
